import { ApiPaths } from 'api/constants';
import {
  CreateScenarioPayload,
  PollingScenarioDetails,
  PollingScenarioGeneral,
} from 'api/endpoints/emailPolling';
import { ProcessGeneral } from 'api/endpoints/processes';
import ApiUrlParams from 'constants/apiUrlParams';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync } from 'container/models';

import {
  CheckCredententialsResult,
  EmailProviderAuthFields,
  EmailProviders,
  GetLabelsResult,
  IEmailScenarioRepo,
  isValidAuthCombination,
} from './IEmailScenarioRepo';

export class EmailScenarioRepo extends BaseRepo implements IEmailScenarioRepo {
  checkCredentials = (
    provider: EmailProviders,
    auth: EmailProviderAuthFields
  ) => {
    if (!isValidAuthCombination(provider, auth))
      return Promise.resolve(
        buildBadRequestApiError(new Error('Authentication fields incomplete.'))
      );

    return this.helpers.handlePost<
      Partial<EmailProviderAuthFields> & { provider: EmailProviders },
      CheckCredententialsResult
    >(ApiPaths.emailprovider.validateCredentials._(), {
      provider,
      ...auth,
    });
  };

  getLabels = (provider: EmailProviders, auth: EmailProviderAuthFields) => {
    if (!isValidAuthCombination(provider, auth))
      return Promise.resolve(
        buildBadRequestApiError(new Error('Authentication fields incomplete.'))
      );

    return this.helpers.handlePost<
      Partial<EmailProviderAuthFields> & { provider: EmailProviders },
      GetLabelsResult
    >(ApiPaths.emailprovider.getLabels._(), {
      provider,
      ...auth,
    });
  };

  getScenarios = (
    processDraft: ProcessGeneral
  ): ApiResponseAsync<DataWithTotal<PollingScenarioGeneral>> =>
    this.helpers.handleGet(
      ApiPaths.processes.byId.inboundEmail.scenarios._({
        [ApiUrlParams.releaseOrDraftProcessId]: processDraft.id,
      })
    );

  getScenarioDetails = (
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<PollingScenarioDetails> =>
    this.helpers.handleGet(
      ApiPaths.processes.byId.inboundEmail.scenarios.byId._({
        [ApiUrlParams.releaseOrDraftProcessId]: scenario.processId,
        [ApiUrlParams.inboundEmailScenarioId]: scenario.id,
      })
    );

  activateScenario = (
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<unknown> =>
    this.helpers.handlePost(
      ApiPaths.processes.byId.inboundEmail.scenarios.byId.activate._({
        [ApiUrlParams.releaseOrDraftProcessId]: scenario.processId,
        [ApiUrlParams.inboundEmailScenarioId]: scenario.id,
      }),
      {}
    );

  deactivateScenario = (
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<unknown> =>
    this.helpers.handlePost(
      ApiPaths.processes.byId.inboundEmail.scenarios.byId.deactivate._({
        [ApiUrlParams.releaseOrDraftProcessId]: scenario.processId,
        [ApiUrlParams.inboundEmailScenarioId]: scenario.id,
      }),
      {}
    );

  deleteScenario = (
    scenario: PollingScenarioGeneral
  ): ApiResponseAsync<unknown> =>
    this.helpers.handleDelete(
      ApiPaths.processes.byId.inboundEmail.scenarios.byId._({
        [ApiUrlParams.releaseOrDraftProcessId]: scenario.processId,
        [ApiUrlParams.inboundEmailScenarioId]: scenario.id,
      })
    );

  createScenario = (
    processDraft: ProcessGeneral,
    newScenarioDetails: CreateScenarioPayload
  ): ApiResponseAsync<PollingScenarioDetails> =>
    this.helpers.handlePost(
      ApiPaths.processes.byId.inboundEmail.scenarios._({
        [ApiUrlParams.releaseOrDraftProcessId]: processDraft.id,
      }),
      newScenarioDetails
    );

  updateScenario = (
    currentScenario: PollingScenarioGeneral,
    updatedScenarioDetails: CreateScenarioPayload
  ): ApiResponseAsync<PollingScenarioDetails> =>
    this.helpers.handlePut(
      ApiPaths.processes.byId.inboundEmail.scenarios.byId._({
        [ApiUrlParams.releaseOrDraftProcessId]: currentScenario.processId,
        [ApiUrlParams.inboundEmailScenarioId]: currentScenario.id,
      }),
      updatedScenarioDetails
    );
}
